import React      from "react";
import { HashRouter as Router, Route } from "react-router-dom";
import {
    debounce as _debounce,
    merge as _merge,
    sortBy as _sortBy
} from 'lodash';
import moment from "moment";
import Uri from "../../../../utils/Uri";

import { SearchApi }         from "../../api/Api";
import { RefluxComponent }   from "sp-react-commons";
import Responsive from "../../../commons/utils/Responsive";
import Navigation from "../../../commons/utils/Navigation";
import { CommonsConstants } from "../../../commons";
import Constants from "../../configurations/Constants";
import Translation from "../../../commons/i18n/Translation";

import SearchDesktopPage from './desktop/SearchDesktopPage';
import SearchMobilePage from './mobile/SearchMobilePage';

const SEARCH_TYPES = [
    { label: Translation.translate("commons.posts"), value: Constants.CONTENT_FILTER.POST },
    { label: Translation.translate("commons.events"), value: Constants.CONTENT_FILTER.EVENT }
];


class SearchPage extends RefluxComponent {

    constructor(props) {
        super(props);
        this.state = this.initialState(props);
        this.debounced = _debounce(this.search, 500);
    }

    initialState = (props) => ({
        comingFrom: this.getComingFromParams(props),
        query: this.getQueryParams(props),
        contentType: this.getContentTypeParams(props),
        group: this.getGroupParams(props),
        language: this.getLanguageParams(props),
        loading: true,
        locality: this.getLocalityParams(props),
        surroundings: this.getSurroundingsParams(props),
        eventDate: this.getEventDateParams(props),
        results: [],
        resultsLocalities: [],
        resultsGroups: [],
        count: undefined, // Not used for the moment
    });

    // Search is done on Props changing, coming from the Router
    // only way to trigger search
    // Change URL to come here
    componentDidUpdate(oldProps) {
        if(
            oldProps.location.search !== this.props.location.search
        ) {
            // Get Locality from Params, but use State if available
            // Avoids empty select box on select
            let locality = this.getLocalityParams(this.props);
            if (locality && this.state.locality && locality.id === this.state.locality.id) {
                locality = this.state.locality;
            }
            // Get Group from Params, but use State if available
            // Avoids empty select box on select
            let group = this.getGroupParams(this.props);
            if (group && this.state.group && group.id === this.state.group.id) {
                group = this.state.group;
            }
            // Get Language from Params, but use State if available
            // Avoids empty select box on select
            let language = this.getLanguageParams(this.props);
            if (language && this.state.language && language.id === this.state.language.id) {
                language = this.state.language;
            }
            this.setState({
                comingFrom: this.getComingFromParams(this.props),
                query: this.getQueryParams(this.props),
                contentType: this.getContentTypeParams(this.props),
                group: group,
                language: language,
                loading: true,
                locality: locality,
                surroundings: this.getSurroundingsParams(this.props),
                eventDate: this.getEventDateParams(this.props),
            }, this.searchDebounce);
        }
    }

    componentDidMount() {
        this.search();
    }

    searchDebounce = () => this.debounced();

    getComingFromParams = (props) => Uri.getQueryParam(props.location.search, "comingFrom") || undefined;
    getQueryParams = (props) => Uri.getQueryParam(props.location.search, "query") ? decodeURIComponent(Uri.getQueryParam(props.location.search, "query")) : "";
    getContentTypeParams = (props) => Uri.getQueryParam(props.location.search, "type") || Constants.CONTENT_FILTER.POST;
    getSurroundingsParams = (props) => Uri.getQueryParam(props.location.search, "surroundings") || Constants.SURROUNDING_FILTER.MUNICIPALITY;
    getLocalityParams = (props) => {
        let localityId = Uri.getQueryParam(props.location.search, "locality"); // Param is called locality but hols only ID
        if(!localityId){
            return null;
        }
        return {id:localityId, name:''};
    };
    getGroupParams = (props) => {
        let groupId = Uri.getQueryParam(props.location.search, "group"); // URL Param is called group but holds only ID
        if(!groupId){
            return null;
        }
        return {id:groupId, name:''};
    };
    getLanguageParams = (props) => {
        let languageId = Uri.getQueryParam(props.location.search, "language"); // URL Param is called group but holds only ID
        return CommonsConstants.LANGUAGES.find(language => language.id === languageId) || null;
    };
    getEventDateParams = (props) => {
        let queryString = Uri.getQueryParam(props.location.search, "eventDate");
        if(!queryString){
            // Set to empty String if not set
            return undefined;
        }
        return queryString;
    };

    search = () => {
        this.setState({
            loading: true,
            resultsLocalities: [],
            resultsGroups: []
        });
        let filters = _merge(
            this.state.query ? {query: this.state.query} : {},
            this.state.contentType ? {contentType: this.state.contentType} : {},
            this.state.group ? {groupId: this.state.group.id } : {},
            this.state.language ? {languageId: this.state.language.id } : {},
            this.state.locality ? {localityId: this.state.locality.id } : {},
            this.state.contentType === Constants.CONTENT_FILTER.EVENT ? { eventDate: this.state.eventDate } : {},
            this.state.surroundings ? { surroundings: this.state.surroundings } : {}
        );
        SearchApi.searchPosts(filters).then((response) => {
            this.setState({
                results: response.teasers,
                count: response.count,
                loading: false,
                locality: response.request.locality ? response.request.locality : null,
                group: response.request.group ? response.request.group : null
            });
        });
        if(this.state.query && this.state.query.length > 2 && !Responsive.isMobile()){
            // Only displayed on Desktop, so avoid api calls on mobile
            SearchApi.searchLocality({ query: this.state.query }).then( (localities) => {
                this.setState({
                    resultsLocalities: _sortBy(localities, 'name')
                });
            });
            SearchApi.searchGroup({ query: this.state.query }).then( (groups) => {
                this.setState({
                    resultsGroups: _sortBy(groups, 'name')
                });
            })
        }
    };

    searchGroup = (query, asyncSelectCallback) => {
        SearchApi.searchGroup({query: query}).then((groups) => {
            asyncSelectCallback(_sortBy(groups, 'name'))
        });
    };

    searchLocality = (query, asyncSelectCallback) => {
        SearchApi.searchLocality({query: query}).then((localities) => {
            asyncSelectCallback(_sortBy(localities, 'name'))
        });
    };

    onChangeQuery = (e) => this.setState({query: e.target.value}, this._changeRoute);
    onChangeContentType = (type) => this.setState({contentType: type.value}, this._changeRoute );
    onChangeGroup = (group) => this.setState({group: group}, this._changeRoute );
    onChangeLocality = (locality) => this.setState({locality: locality}, this._changeRoute );
    onSetLocality = (locality) => this.setState({group: null, locality: locality, query: "", }, this._changeRoute );
    onSetGroup = (group) => this.setState({group: group, locality: null, query: "", }, this._changeRoute );
    onChangeEventDate = (value) => {
        let parsedDate = moment(value, CommonsConstants.SIMPLE_DATE_DISPLAY_FORMAT[Translation.getLanguageName()], true);
        if(parsedDate.isValid()) {
            let formattedDate = parsedDate.format(CommonsConstants.SIMPLE_DATE_API_FORMAT);
            this.setState({eventDate: formattedDate}, this._changeRoute);
        } else {
            this.setState({eventDate: undefined}, this._changeRoute);
        }
    };
    onChangeHTML5EventDate = (ev) => {
        if(moment(ev.target.value, CommonsConstants.SIMPLE_DATE_API_FORMAT).isValid())
            this.setState({eventDate: ev.target.value }, this._changeRoute );
        else {
            this.setState({eventDate: ""}, this._changeRoute );
        }
    };
    onChangeSurroundings = (val) => this.setState({surroundings: val.value}, this._changeRoute );
    onSubmit = (ev, input) => {
        input.blur();
    };

    onResetFilters = () => {
        this.setState({
            comingFrom: undefined,
            query: "",
            group: null,
            language: null,
            locality: null,
            eventDate: "",
            loading:true,
            results: []
        }, this._changeRoute)
    };

    onChangeLanguage = (lang) => {
        this.setState( {language: lang}, this._changeRoute );
    };

    _changeRoute = () => {
        let query = _merge(
            this.state.query ? { query: this.state.query } : {},
            this.state.comingFrom ? { comingFrom: this.state.comingFrom } : {},
            this.state.contentType ? { type: this.state.contentType } : {},
            this.state.locality ? { locality: this.state.locality.id } : {},
            this.state.locality ? { surroundings: this.state.surroundings } : {},
            this.state.group ? { group: this.state.group.id } : {},
            this.state.contentType === Constants.CONTENT_FILTER.EVENT ? { eventDate: this.state.eventDate } : {},
            this.state.language ? { language: this.state.language.id } : {}
        );

        Navigation.goTo(Uri.buildQuery("/", query), this.props.history);
    };

    getCurrentQueryObject = () => {
        let obj = {}; //Posts and events have different parameters that are used
        obj.post = {};
        obj.event = {};
        if (this.state.query) {
            obj.post.query = encodeURIComponent(this.state.query);
            obj.event.query = encodeURIComponent(this.state.query);
        }
        if (this.state.locality) {
            obj.post.locality = encodeURIComponent(this.state.locality.id);
            obj.event.locality = encodeURIComponent(this.state.locality.id);
        }
        if (this.state.surroundings) {
            obj.post.surroundings = encodeURIComponent(this.state.surroundings);
            obj.event.surroundings = encodeURIComponent(this.state.surroundings);
        }
        if (this.state.group) {
            obj.post.group = encodeURIComponent(this.state.group.id);
            obj.event.group = encodeURIComponent(this.state.group.id);
        }
        // There is always a date for events
        obj.event.eventDate = encodeURIComponent(this.state.eventDate);

        return obj;
    };

    render = () => {
        if(Responsive.isMobile()){
            return <SearchMobilePage
                comingFrom={this.state.comingFrom}
                types={SEARCH_TYPES}
                contentType={this.state.contentType}
                locality={this.state.locality}
                searchLocality={this.searchLocality}
                onChangeLocality={this.onChangeLocality}
                group={this.state.group}
                searchGroup={this.searchGroup}
                onChangeGroup={this.onChangeGroup}
                eventDate={this.state.eventDate}
                onChangeEventDate={this.onChangeHTML5EventDate}
                surroundings={this.state.surroundings}
                onChangeSurroundings={this.onChangeSurroundings}
                query={this.state.query}
                onChangeQuery={this.onChangeQuery}
                currentQueryObject={this.getCurrentQueryObject()}
                results={this.state.results}
                loading={this.state.loading}
                onSubmit={this.onSubmit}
                onResetFilters={this.onResetFilters}
                language={this.state.language}
                onChangeLanguage={this.onChangeLanguage}
            />
        }
        return <SearchDesktopPage
            comingFrom={this.state.comingFrom}
            query={this.state.query}
            onChangeQuery={this.onChangeQuery}
            types={SEARCH_TYPES}
            contentType={this.state.contentType}
            onChangeContentType={this.onChangeContentType}
            locality={this.state.locality}
            searchLocality={this.searchLocality}
            onChangeLocality={this.onChangeLocality}
            group={this.state.group}
            searchGroup={this.searchGroup}
            onChangeGroup={this.onChangeGroup}
            onSetGroup={this.onSetGroup}
            onSetLocality={this.onSetLocality}
            eventDate={this.state.eventDate}
            onChangeEventDate={this.onChangeEventDate}
            surroundings={this.state.surroundings}
            onChangeSurroundings={this.onChangeSurroundings}
            currentQueryObject={this.getCurrentQueryObject()}
            results={this.state.results}
            resultsLocalities={this.state.resultsLocalities}
            resultsGroups={this.state.resultsGroups}
            loading={this.state.loading}
            onResetFilters={this.onResetFilters}
            language={this.state.language}
            onChangeLanguage={this.onChangeLanguage}
        />
    };
}


export default class SearchPageRouter extends React.Component {
    render = () => (
        <Router>
            <Route path="/" component={SearchPage} />
        </Router>
    );
}



// WEBPACK FOOTER //
// ./js/mywort/frontend/pages/search/SearchPage.jsx